/* eslint-disable no-console */
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import { makeStyles } from '@material-ui/core/styles';
import { useState, useEffect, useContext } from 'react';
import API from '@aws-amplify/api';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import Chip from '@material-ui/core/Chip';
import DateFnsUtils from '@date-io/date-fns';
import Divider from '@material-ui/core/Divider';
import capitalize from '../../../utils/capitalize';
import TextField from '@material-ui/core/TextField';
import CardHeader from '@material-ui/core/CardHeader';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import EmiconContext from '../../context/EmiconContext';
import Feedback from '../../../emilog/components/Feedback';
import AuthContext from '../../../emilog/context/AuthContext';
import LoaderButton from '../../../emilog/components/LoaderButton';

const useStyles = makeStyles((theme) => ({
  card: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%'
  },
  cardContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'stretch', // Make children elements stretch to fill the width
    flexGrow: 1, // Allows cardContent to expand
  },
  datePicker: {
    width: '100%',
    marginTop: '0px',
  },
  datePickersBox: {
    display: 'flex',
    marginBottom: '8px',
  },
  cardActions: {
    justifyContent: 'flex-end',
    paddingRight: theme.spacing(2),
  },
  servicePlanned: {
    background: 'green',
    color: 'white',
  },
  serviceNotPlanned: {
    background: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(0, 0, 0, 0.26)',
  },
}));

interface EmiconKatWechselGeplantProps {
  hasKatPlanned: boolean | null
  lastKatPlannedDate: number | null
}

export default function EmiconKatWechselGeplant({ hasKatPlanned, lastKatPlannedDate }: EmiconKatWechselGeplantProps) {
  const classes = useStyles();
  const { currentUser } = useContext(AuthContext);
  const { currentDevice } = useContext(EmiconContext);

  const [firstName, lastName] = currentUser?.user.attributes.email.split('@')[0].split('.') ?? ['', ''];
  const author = `${capitalize(firstName)} ${capitalize(lastName)}`;
  const active = currentDevice.serialNumber !== undefined;

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [reasonText, setReasonText] = useState<string | null>('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);


  async function handleSubmit() {
    try {
      const dateNow = Math.round(Date.now() / 1000); // number
      const timestampStart = startDate ? Math.round(startDate.getTime() / 1000) : null;
      const timestampEnd = endDate ? Math.round(endDate.getTime() / 1000) : null;
      if (timestampStart === null || timestampEnd === null || lastKatPlannedDate === null) return;

      const endOfDayTimestamp = Math.floor(new Date(timestampEnd * 1000).setHours(23, 59, 59, 999) / 1000);

      setLoading(true);
      setError(false);
      setSuccess(false);

      /* 
        Letzte geplante Katalysatorwechsel in EMI-LOG-Infos Tabelle hinzufügen.
        macht das spätere filtern nach der Spalte, ob eine Wartung geplant ist, einfacher.
        Der spätere Timestamp ist für die Tabelle ausschlaggebend, darum der End und nicht merh der Start Timestamp
      */

      if (timestampEnd > lastKatPlannedDate) { // Was wenn Katalysatorwechsel vorgezogen wird?
        await API.post('emilog', 'updateInfos', {
          body: {
            serialNumber: currentDevice.serialNumber,
            isEmicon: true, // ACHTUNG
            fields: {
              KatWechselGeplant: endOfDayTimestamp,
              /*
                Zeitpunkt nutzen um Timeframe für Standalone Lambdas (Mailversand) zu erhalten um Mails einmalig auch bei geplanten Katalysatorwechsel zu verschicken.
                Wird bei Abschluss wie KatWechselGeplant auf 0 gesetzt um den Zeitraum bei nicht geplantem Katalysatorwechsel wieder zu ignorieren.
              */
              KatWechselAngelegt: dateNow
            },
          },
        });
      }

      let reason = `Katalysatorwechsel geplant: ${reasonText}`;

      await API.post('emilog', 'addServiceEntry', {
        body: {
          serialNumber: currentDevice.serialNumber,
          timestampStart,
          timestampEnd,
          author,
          reason,
          isEmicon: true,
        },
      });

      setSuccess(true);
      setLoading(false);
    } catch (err) {
      setError(true);
      setLoading(false);
      console.log(err);
    }
  }

  useEffect(() => {
    setSuccess(false);
    setError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDevice.serialNumber]);

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Card className={classes.card}>
        <CardHeader
          title="Katalysatorwechsel geplant"
          action={hasKatPlanned !== null ? (
            <Chip
              className={hasKatPlanned ? classes.servicePlanned : classes.serviceNotPlanned}
              label={hasKatPlanned ? 'Katalysatorwechsel geplant' : 'Kein Katalysatorwechsel geplant'}
            />
          ) : (
            null
          )}
        />
        <Divider />
        <CardContent className={classes.cardContent}>
          <Box>
            <Box className={classes.datePickersBox}>
              <KeyboardDatePicker
                size="small"
                value={startDate}
                className={classes.datePicker}
                style={{ marginRight: '16px' }}
                disabled={!active}
                disableToolbar
                autoOk
                minDate={new Date()}
                variant="inline"
                format="dd/MM/yyyy"
                margin="normal"
                id="date-picker-inline"
                label="von"
                onChange={(e) => setStartDate(e)}
                invalidDateMessage="Kein korrektes Format"
                inputVariant="outlined"
              />
              <KeyboardDatePicker
                size="small"
                value={endDate}
                className={classes.datePicker}
                disabled={!active}
                disableToolbar
                autoOk
                minDate={new Date()}
                variant="inline"
                format="dd/MM/yyyy"
                margin="normal"
                id="date-picker-inline"
                label="bis"
                onChange={(e) => setEndDate(e)}
                invalidDateMessage="Kein korrektes Format"
                inputVariant="outlined"
              />
            </Box>
            <TextField
              size="small"
              label="Grund"
              disabled={!active}
              variant="outlined"
              fullWidth
              type="text"
              value={reasonText !== null ? reasonText : ''}
              onChange={(e) => setReasonText(e.target.value)}
            />
          </Box>
        </CardContent>
        <Divider />
        <CardActions className={classes.cardActions}>
          <Feedback error={error} success={success} />
          <LoaderButton
            isLoading={loading}
            text="Absenden"
            onClick={handleSubmit}
            disabled={!active || startDate === null || endDate === null || reasonText === '' || hasKatPlanned === true}
          />
        </CardActions>
      </Card>
    </MuiPickersUtilsProvider>
  );
}
